"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DmsTableHeader = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _reactIntlUniversal = _interopRequireDefault(require("react-intl-universal"));
var _material = require("@mui/material");
var _SwapVert = _interopRequireDefault(require("@mui/icons-material/SwapVert"));
var _DmsPermissions = require("../DmsPermissions/DmsPermissions");
var _DmsDateTimeRangePicker = require("../DmsDateTimeRangePicker/DmsDateTimeRangePicker");
var _DmsDateRangePicker = require("../DmsDateRangePicker/DmsDateRangePicker");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Table header component for displaying sortable columns.
 * @param {Object} props - Component props.
 * @param {Array} props.header - Array of column headers.
 * @param {Object} props.sort - Current sorting state.
 * @param {Function} props.onSort - Function to handle sorting.
 * @param {string} props.ariaControl - ARIA control identifier.
 * @param {Function} props.handleFilter - Function to handle filtering.
 * @returns {JSX.Element} TableHead component with sortable column headers.
 */
const DmsTableHeader = _ref => {
  let {
    header,
    sort,
    onSort,
    ariaControl,
    handleFilter,
    fieldtype
  } = _ref;
  const [selectedFilters, setSelectedFilters] = (0, _react.useState)({});

  /**
   * Render the header cell.
   * @param {Object} col - Column definition.
   * @param {number} index - Column index.
   * @returns {JSX.Element|null} Rendered TableCell or null if hidden or no permissions.
   */
  const headerCellRender = (col, index) => {
    var _sort$sortColumn, _sort$sortColumn2;
    const {
      hidden,
      permissions,
      order_column_name,
      column_name,
      label,
      sortable
    } = col;
    if (hidden || permissions && !(0, _DmsPermissions.havePermissions)(permissions)) {
      return null;
    }
    const createSortHandler = () => {
      const sortColumn = order_column_name || column_name;
      const sortOrder = sort[sortColumn] === "ASC" ? "DESC" : "ASC";
      onSort(col, sortOrder);
    };
    const sortColumn = order_column_name || column_name;
    return /*#__PURE__*/_react.default.createElement(_material.TableCell, {
      key: index,
      sortDirection: ((_sort$sortColumn = sort[sortColumn]) === null || _sort$sortColumn === void 0 ? void 0 : _sort$sortColumn.toLowerCase()) || false,
      variant: "head",
      sx: {
        wordBreak: 'keep-all',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
      }
    }, sortable !== false ? /*#__PURE__*/_react.default.createElement(_material.TableSortLabel, {
      active: !!sort[sortColumn],
      direction: ((_sort$sortColumn2 = sort[sortColumn]) === null || _sort$sortColumn2 === void 0 ? void 0 : _sort$sortColumn2.toLowerCase()) || "asc",
      onClick: createSortHandler,
      IconComponent: sort[sortColumn] ? undefined : _SwapVert.default,
      hideSortIcon: !!sort[sortColumn],
      sx: {
        "& .MuiTableSortLabel-icon": {
          opacity: 1
        }
      }
    }, label, " ", fieldtype && "(".concat(fieldtype, ")")) : "".concat(label, " ").concat(fieldtype ? "(".concat(fieldtype, ")") : ""));
  };
  const filtable = header.some(column => column.filter);

  /**
   * Render the filter cell.
   * @param {Object} col - Column definition.
   * @param {number} index - Column index.
   * @returns {JSX.Element|null} Rendered TableCell or null if hidden or no permissions.
   */
  const filterCellRender = (col, index) => {
    const {
      hidden,
      permissions,
      column_name,
      control,
      options,
      map,
      width,
      properties,
      empty_option
    } = col;
    const {
      filter
    } = col;
    if (hidden || permissions && !(0, _DmsPermissions.havePermissions)(permissions)) {
      return null;
    }
    if (!filter || !column_name) {
      return /*#__PURE__*/_react.default.createElement(_material.TableCell, {
        key: index
      });
    }
    const handleInputChange = (name, value) => {
      setSelectedFilters(prevState => {
        const newFilters = _objectSpread({}, prevState);
        if (value === "") {
          delete newFilters[name];
        } else {
          newFilters[name] = value;
        }
        return newFilters;
      });
      handleFilter(name, value === "" ? null : value, properties);
    };
    let inputComponent = null;
    switch (control) {
      case "select":
        const formattedOptions = Array.isArray(options) ? options : map && Object.keys(map).length > 0 ? Object.entries(map).map(_ref2 => {
          let [key, value] = _ref2;
          return {
            label: value,
            value: isNaN(key) ? key : parseFloat(key)
          };
        }) : [];
        inputComponent = /*#__PURE__*/_react.default.createElement(_material.TextField, {
          select: true,
          fullWidth: true,
          name: "filter-".concat(column_name),
          id: "filter-".concat(column_name),
          value: ((_properties$filter$fi, _properties$filter, _selectedFilters$name) => {
            const name = (_properties$filter$fi = properties === null || properties === void 0 || (_properties$filter = properties.filter) === null || _properties$filter === void 0 ? void 0 : _properties$filter.field) !== null && _properties$filter$fi !== void 0 ? _properties$filter$fi : column_name;
            return (_selectedFilters$name = selectedFilters[name]) !== null && _selectedFilters$name !== void 0 ? _selectedFilters$name : "";
          })(),
          onChange: event => {
            var _properties$filter$fi2, _properties$filter2;
            const name = (_properties$filter$fi2 = properties === null || properties === void 0 || (_properties$filter2 = properties.filter) === null || _properties$filter2 === void 0 ? void 0 : _properties$filter2.field) !== null && _properties$filter$fi2 !== void 0 ? _properties$filter$fi2 : column_name;
            const value = event.target.value;
            const newValue = selectedFilters[name] == value ? "" : value;
            setSelectedFilters(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
              [name]: newValue
            }));
            handleFilter(name, newValue === "" ? null : newValue, properties);
          },
          SelectProps: {
            displayEmpty: true,
            renderValue: selected => {
              if (selected === "" || selected === null || selected === undefined) {
                return /*#__PURE__*/_react.default.createElement("em", null, _reactIntlUniversal.default.get("SelectAnOption").d("Select an option..."));
              }
              const option = formattedOptions.find(opt => String(opt.value) === String(selected));
              return option ? option.label : selected;
            }
          }
        }, formattedOptions.map(option => /*#__PURE__*/_react.default.createElement(_material.MenuItem, {
          key: option.value,
          value: option.value,
          onClick: event => {
            var _properties$filter$fi3, _properties$filter3;
            const name = (_properties$filter$fi3 = properties === null || properties === void 0 || (_properties$filter3 = properties.filter) === null || _properties$filter3 === void 0 ? void 0 : _properties$filter3.field) !== null && _properties$filter$fi3 !== void 0 ? _properties$filter$fi3 : column_name;
            const currentValue = selectedFilters[name];

            // If clicking on already selected value, clear it
            if (String(currentValue) === String(option.value)) {
              event.preventDefault();
              event.stopPropagation();
              setSelectedFilters(prevState => {
                const newFilters = _objectSpread({}, prevState);
                delete newFilters[name];
                return newFilters;
              });
              handleFilter(name, null, properties);
            }
          }
        }, option.label)));
        break;
      case "date":
        inputComponent = /*#__PURE__*/_react.default.createElement(_DmsDateRangePicker.DmsDateRangePicker, {
          name: "filter-".concat(column_name),
          className: "calendar-container",
          id: "filter-".concat(column_name),
          onChange: _ref3 => {
            var _properties$filter$fi4, _properties$filter4;
            let {
              startDate,
              endDate
            } = _ref3;
            const name = (_properties$filter$fi4 = properties === null || properties === void 0 || (_properties$filter4 = properties.filter) === null || _properties$filter4 === void 0 ? void 0 : _properties$filter4.field) !== null && _properties$filter$fi4 !== void 0 ? _properties$filter$fi4 : column_name;
            let value;
            if (startDate && endDate) {
              const dateObject = {
                startDate,
                endDate
              };
              value = JSON.stringify(dateObject);
            } else {
              value = "";
            }
            setSelectedFilters(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
              [name]: value
            }));
            handleFilter(name, value === "" ? null : value, properties);
          }
        });
        break;
      case "datetime":
        inputComponent = /*#__PURE__*/_react.default.createElement(_DmsDateTimeRangePicker.DmsDateTimeRangePicker, {
          name: "filter-".concat(column_name),
          className: "calendar-container",
          id: "filter-".concat(column_name),
          onChange: _ref4 => {
            var _properties$filter$fi5, _properties$filter5;
            let {
              startDate,
              endDate
            } = _ref4;
            const name = (_properties$filter$fi5 = properties === null || properties === void 0 || (_properties$filter5 = properties.filter) === null || _properties$filter5 === void 0 ? void 0 : _properties$filter5.field) !== null && _properties$filter$fi5 !== void 0 ? _properties$filter$fi5 : column_name;
            let value;
            if (startDate && endDate) {
              const dateObject = {
                startDate,
                endDate
              };
              value = JSON.stringify(dateObject);
            } else {
              value = "";
            }
            setSelectedFilters(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
              [name]: value
            }));
            handleFilter(name, value === "" ? null : value, properties);
          }
        });
        break;
      default:
        inputComponent = /*#__PURE__*/_react.default.createElement(_material.TextField, {
          type: "text",
          fullWidth: true,
          name: "filter-".concat(column_name),
          id: "filter-".concat(column_name),
          variant: "outlined",
          value: selectedFilters[column_name] || "",
          onChange: event => {
            var _properties$filter$fi6, _properties$filter6;
            const name = (_properties$filter$fi6 = properties === null || properties === void 0 || (_properties$filter6 = properties.filter) === null || _properties$filter6 === void 0 ? void 0 : _properties$filter6.field) !== null && _properties$filter$fi6 !== void 0 ? _properties$filter$fi6 : column_name;
            const value = event.target.value;
            setSelectedFilters(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
              [name]: value
            }));
            handleFilter(name, value === "" ? null : value, properties);
          }
        });
        break;
    }
    return /*#__PURE__*/_react.default.createElement(_material.TableCell, {
      key: index,
      tabIndex: "0",
      rowSpan: 1,
      colSpan: 1,
      "aria-controls": ariaControl,
      sx: {
        width: width
      }
    }, inputComponent);
  };
  return /*#__PURE__*/_react.default.createElement(_material.TableHead, null, /*#__PURE__*/_react.default.createElement(_material.TableRow, null, header.map((col, index) => headerCellRender(col, index))), filtable && /*#__PURE__*/_react.default.createElement(_material.TableRow, null, header.map((col, index) => filterCellRender(col, index))));
};
exports.DmsTableHeader = DmsTableHeader;
DmsTableHeader.propTypes = {
  header: _propTypes.default.arrayOf(_propTypes.default.shape({
    hidden: _propTypes.default.bool,
    permissions: _propTypes.default.arrayOf(_propTypes.default.string),
    order_column_name: _propTypes.default.string,
    column_name: _propTypes.default.string.isRequired,
    label: _propTypes.default.string.isRequired,
    sortable: _propTypes.default.bool,
    filter: _propTypes.default.bool,
    control: _propTypes.default.string,
    empty_option: _propTypes.default.bool,
    options: _propTypes.default.arrayOf(_propTypes.default.shape({
      label: _propTypes.default.string,
      value: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number])
    })),
    map: _propTypes.default.object,
    width: _propTypes.default.string,
    properties: _propTypes.default.shape({
      filter: _propTypes.default.shape({
        field: _propTypes.default.string,
        value: _propTypes.default.string
      })
    })
  })).isRequired,
  sort: _propTypes.default.object.isRequired,
  onSort: _propTypes.default.func.isRequired,
  ariaControl: _propTypes.default.string.isRequired,
  handleFilter: _propTypes.default.func.isRequired
};